/**
 * MergeFieldSuggest
 *
 * The `{`-triggered merge-field autocomplete for plain `<input>` fields, so
 * single-line fields (email To/CC/BCC, subject lines) behave like the rich
 * MergeFieldEditor body: type `{`, filter, pick a field.
 *
 * Usage:
 *   const suggest = useMergeFieldSuggest({ tokens, anchorRef, onSelect });
 *   <input
 *     onChange={(e) => { setValue(e.target.value); suggest.sync(e.target.value, e.target.selectionStart); }}
 *     onKeyDown={(e) => { if (suggest.handleKeyDown(e)) return; ... }}
 *     onBlur={() => suggest.close()}
 *     {...suggest.inputProps}
 *   />
 *   {suggest.dropdown}
 */

import * as React from 'react';
import * as ReactDOM from 'react-dom';
import { cn } from '@/lib/utils';
import { filterTokens, tokenDisplayName, type MergeTokenDef } from '@/services/mergeTokens';

let uid = 0;
const nextUid = () => `mfs-${(uid += 1)}`;

/** Characters allowed inside a token name while the autocomplete is open. */
const TOKEN_QUERY_RE = /\{([A-Za-z0-9._]*)$/;

export interface MergeFieldSuggestMatch {
  /** Index of the opening `{` in the field value. */
  start: number;
  /** Caret position when the match was computed. */
  end: number;
  /** Text typed after the `{`. */
  query: string;
}

interface SuggestDropdownProps {
  tokens: MergeTokenDef[];
  query: string;
  anchorRef: React.RefObject<HTMLElement | null>;
  selectedIndex: number;
  listboxId: string;
  optionId: (index: number) => string;
  onSelect: (token: MergeTokenDef) => void;
}

function SuggestDropdown({
  tokens,
  query,
  anchorRef,
  selectedIndex,
  listboxId,
  optionId,
  onSelect,
}: SuggestDropdownProps) {
  const [position, setPosition] = React.useState<{ top: number; left: number } | null>(null);

  React.useLayoutEffect(() => {
    const reposition = () => {
      const el = anchorRef.current;
      if (!el) return;
      const rect = el.getBoundingClientRect();
      setPosition({ top: rect.bottom + 4, left: rect.left });
    };
    reposition();
    window.addEventListener('scroll', reposition, true);
    window.addEventListener('resize', reposition);
    return () => {
      window.removeEventListener('scroll', reposition, true);
      window.removeEventListener('resize', reposition);
    };
  }, [anchorRef]);

  if (!position) return null;

  return ReactDOM.createPortal(
    <div
      className="fixed z-[9999] w-72 rounded-md border border-border bg-popover shadow-lg overflow-hidden"
      style={{ top: position.top, left: position.left }}
    >
      <div className="px-2 py-1.5 border-b border-border bg-muted/50">
        <span className="text-xs text-muted-foreground font-medium">
          {query ? `Matching "${query}"` : 'Type to filter variables…'}
        </span>
      </div>
      <ul
        id={listboxId}
        role="listbox"
        aria-label="Merge field suggestions"
        className="max-h-56 overflow-y-auto py-1"
      >
        {tokens.map((token, i) => (
          <li
            key={token.token}
            id={optionId(i)}
            role="option"
            aria-selected={i === selectedIndex}
            aria-label={`${tokenDisplayName(token)}. ${token.description}`}
          >
            <button
              type="button"
              tabIndex={-1}
              className={cn(
                'w-full text-left px-3 py-1.5 flex flex-col gap-0.5 hover:bg-accent',
                i === selectedIndex && 'bg-accent',
              )}
              onMouseDown={(e) => {
                e.preventDefault(); // keep the field focused
                onSelect(token);
              }}
            >
              <span className="flex items-center justify-between gap-2">
                <span className="text-sm font-medium leading-tight">{tokenDisplayName(token)}</span>
                <code className="text-[10px] text-muted-foreground/80 font-mono shrink-0">
                  {token.token}
                </code>
              </span>
              {token.description && (
                <span className="text-xs text-muted-foreground leading-tight">
                  {token.description}
                </span>
              )}
            </button>
          </li>
        ))}
      </ul>
      <div className="px-2 py-1 border-t border-border bg-muted/30">
        <span className="text-[10px] text-muted-foreground">
          ↑↓ navigate · Enter/Tab select · Esc dismiss
        </span>
      </div>
    </div>,
    document.body,
  );
}

export interface UseMergeFieldSuggestOptions {
  /** Scoped token list — e.g. `getMergeTokensForScope('email')`. */
  tokens: MergeTokenDef[];
  /** Element the dropdown is positioned under. */
  anchorRef: React.RefObject<HTMLElement | null>;
  /** Called when a field is picked, with the `{query` range it should replace. */
  onSelect: (token: MergeTokenDef, match: MergeFieldSuggestMatch) => void;
  /** When true the autocomplete never opens. */
  disabled?: boolean;
}

export interface UseMergeFieldSuggestResult {
  isOpen: boolean;
  /** Recompute the match from the field value + caret position. */
  sync: (value: string, caret: number | null) => void;
  /** Dismiss the dropdown. */
  close: () => void;
  /** Handles navigation / selection keys. Returns true when the key was consumed. */
  handleKeyDown: (e: React.KeyboardEvent) => boolean;
  /** aria wiring for the input element. */
  inputProps: React.AriaAttributes & { role?: 'combobox' };
  dropdown: React.ReactNode;
}

export function useMergeFieldSuggest({
  tokens,
  anchorRef,
  onSelect,
  disabled,
}: UseMergeFieldSuggestOptions): UseMergeFieldSuggestResult {
  const [match, setMatch] = React.useState<MergeFieldSuggestMatch | null>(null);
  const [selectedIndex, setSelectedIndex] = React.useState(0);

  const listboxId = React.useRef(nextUid()).current;
  const optionId = React.useCallback((i: number) => `${listboxId}-opt-${i}`, [listboxId]);

  const filtered = React.useMemo(
    () => (match ? filterTokens(tokens, match.query) : []),
    [match, tokens],
  );
  const clampedIndex = Math.min(selectedIndex, Math.max(0, filtered.length - 1));
  const isOpen = match !== null && filtered.length > 0;

  const close = React.useCallback(() => setMatch(null), []);

  const sync = React.useCallback(
    (value: string, caret: number | null) => {
      if (disabled) return;
      const end = caret ?? value.length;
      const found = TOKEN_QUERY_RE.exec(value.slice(0, end));
      if (!found) {
        setMatch(null);
        return;
      }
      setMatch({ start: end - found[0].length, end, query: found[1] });
      setSelectedIndex(0);
    },
    [disabled],
  );

  const handleKeyDown = React.useCallback(
    (e: React.KeyboardEvent) => {
      if (!isOpen || !match) return false;
      switch (e.key) {
        case 'ArrowDown':
          e.preventDefault();
          setSelectedIndex(Math.min(filtered.length - 1, clampedIndex + 1));
          return true;
        case 'ArrowUp':
          e.preventDefault();
          setSelectedIndex(Math.max(0, clampedIndex - 1));
          return true;
        case 'Enter':
        case 'Tab': {
          const token = filtered[clampedIndex];
          if (!token) return false;
          e.preventDefault();
          setMatch(null);
          onSelect(token, match);
          return true;
        }
        case 'Escape':
          e.preventDefault();
          setMatch(null);
          return true;
        default:
          return false;
      }
    },
    [isOpen, match, filtered, clampedIndex, onSelect],
  );

  const handleSelect = React.useCallback(
    (token: MergeTokenDef) => {
      if (!match) return;
      setMatch(null);
      onSelect(token, match);
    },
    [match, onSelect],
  );

  return {
    isOpen,
    sync,
    close,
    handleKeyDown,
    inputProps: isOpen
      ? {
          role: 'combobox',
          'aria-expanded': true,
          'aria-controls': listboxId,
          'aria-activedescendant': optionId(clampedIndex),
        }
      : {},
    dropdown:
      isOpen && match ? (
        <SuggestDropdown
          tokens={filtered}
          query={match.query}
          anchorRef={anchorRef}
          selectedIndex={clampedIndex}
          listboxId={listboxId}
          optionId={optionId}
          onSelect={handleSelect}
        />
      ) : null,
  };
}
